import { cn } from "@/dentlab/src/lib/util";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { Plus, Maximize2, Minimize2, Download } from "lucide-react";
import { useState } from "react";
import { useStorage } from "@/dentlab/src/hooks/useStorage";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from "@/dentlab/src/components/ui/dropdown-menu";

interface FileTabActionsProps {
	isExpanded?: boolean;
	onToggleExpand?: () => void;
}

export const FileTabActions: React.FC<FileTabActionsProps> = ({
	isExpanded,
	onToggleExpand,
}) => {
	const { job, uploadFile, labJobFilesLookup, medJobFilesLookup } =
		useJobStore((state) => ({
			job: state.job,
			uploadFile: state.uploadFile,
			labJobFilesLookup: state.labJobFilesLookup,
			medJobFilesLookup: state.medJobFilesLookup,
		}));

	const { downloadFromSupabase } = useStorage();
	const [uploading, setUploading] = useState(false);

	const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files || e.target.files?.length <= 0 || !job?.id) return;
		setUploading(true);
		const files = Array.from(e.target.files);
		for (const file of files) {
			await uploadFile(file, job.id);
		}
		setUploading(false);
		e.target.value = "";
	};

	const labFiles = Object.values(labJobFilesLookup).filter(
		(f) => !f.job_document_id
	);
	const medFiles = Object.values(medJobFilesLookup);

	const handleDownload = async (scope: "all" | "lab" | "med") => {
		const filesToDownload =
			scope === "lab"
				? labFiles
				: scope === "med"
					? medFiles
					: [...labFiles, ...medFiles];
		for (const file of filesToDownload) {
			if (file.path_name) {
				await downloadFromSupabase(file.path_name);
			}
		}
	};

	const allFileCount = labFiles.length + medFiles.length;

	const btnClass =
		"h-5 w-5 p-0 flex items-center justify-center rounded hover:bg-black/10 transition-colors cursor-pointer";

	return (
		<div className="flex items-center gap-0.5">
			<TooltipWrapper title="Datei hochladen">
				<label
					htmlFor="job-file-header-upload"
					className={cn(
						btnClass,
						uploading && "cursor-wait opacity-50"
					)}
				>
					<Plus className="h-3.5 w-3.5" />
				</label>
			</TooltipWrapper>
			<input
				disabled={uploading}
				type="file"
				multiple
				className="hidden"
				id="job-file-header-upload"
				onChange={handleFileUpload}
			/>

			{allFileCount > 0 && (
				<DropdownMenu>
					<TooltipWrapper title="Dateien herunterladen">
						<DropdownMenuTrigger asChild>
							<button
								className={btnClass}
								aria-label="Dateien herunterladen"
								data-testid="job-files-download-trigger"
							>
								<Download className="h-3 w-3" />
							</button>
						</DropdownMenuTrigger>
					</TooltipWrapper>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={() => handleDownload("all")}
							disabled={allFileCount === 0}
							data-testid="job-files-download-all"
						>
							Alle Dateien ({allFileCount})
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => handleDownload("lab")}
							disabled={labFiles.length === 0}
							data-testid="job-files-download-lab"
						>
							Nur Labor ({labFiles.length})
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => handleDownload("med")}
							disabled={medFiles.length === 0}
							data-testid="job-files-download-med"
						>
							Nur Praxis ({medFiles.length})
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)}

			{onToggleExpand && (
				<TooltipWrapper
					title={isExpanded ? "Verkleinern" : "Vergrössern"}
				>
					<button className={btnClass} onClick={onToggleExpand}>
						{isExpanded ? (
							<Minimize2 className="h-3 w-3" />
						) : (
							<Maximize2 className="h-3 w-3" />
						)}
					</button>
				</TooltipWrapper>
			)}
		</div>
	);
};
